/**
 * Versioned migration of persisted localStorage state.
 *
 * Persisted data outlives the code that wrote it: a user's cache can hold a
 * shape from any release they've loaded before. Rather than guess at that shape,
 * every persisted blob is stamped with the schema version that produced it and
 * each schema declares transformers that carry state one version forward. A user
 * three releases behind is walked up to today's shape one step at a time, so a
 * rename can *preserve* the user's value (`employeeNameFilter` -> `employeeFilter`)
 * instead of dropping it.
 *
 * `reconcileWithDefaults` still runs last as a backstop. A missing transformer, a
 * gap in the chain, or data written by a *newer* release (a rolled-back deploy)
 * must never hand the app a shape that crashes on first property access.
 */

import { isPlainObject, reconcileWithDefaults } from './reconcileWithDefaults'

/**
 * A transformer from the state as written at version N-1 to the shape of version
 * N. Intermediate versions have no TypeScript type — only the latest schema does
 * — so transformers trade in loose records.
 */
export type SchemaMigration = (
  previous: Record<string, unknown>,
) => Record<string, unknown>

/**
 * Transformers keyed by the target version each one produces: `migrations[2]`
 * takes a v1 blob to v2. Keys need not be contiguous, but gaps are skipped
 * silently, so keep the chain complete.
 */
export type SchemaMigrations = Record<number, SchemaMigration>

interface VersionedEnvelope {
  __schemaVersion: number
  state: unknown
}

const isEnvelope = (value: unknown): value is VersionedEnvelope =>
  isPlainObject(value) &&
  typeof value.__schemaVersion === 'number' &&
  // A corrupted cache could hold NaN/Infinity, which would poison the migration
  // loop's bounds; only a finite, non-negative version is a real envelope.
  Number.isInteger(value.__schemaVersion) &&
  value.__schemaVersion >= 0 &&
  'state' in value

/**
 * Wrap state for persistence, stamping it with the schema version that produced
 * it so a later release knows where to resume migrating from.
 */
export const withVersion = <T>(
  version: number,
  state: T,
): VersionedEnvelope => ({
  __schemaVersion: version,
  state,
})

export const migrateToLatest = <T extends object>(
  persisted: unknown,
  {
    version,
    defaults,
    migrations = {},
  }: { version: number; defaults: T; migrations?: SchemaMigrations },
): T => {
  // Anything without an envelope predates versioning, so it starts at version 0.
  const storedVersion = isEnvelope(persisted) ? persisted.__schemaVersion : 0
  const payload = isEnvelope(persisted) ? persisted.state : persisted

  let migrated = payload
  for (let target = storedVersion + 1; target <= version; target++) {
    const migrate = migrations[target]
    if (migrate && isPlainObject(migrated)) migrated = migrate(migrated)
  }

  return reconcileWithDefaults(migrated, defaults)
}
